import {useApolloClient} from '@apollo/client';
import {none, some} from '@danwang/option';
import {
  DepositoryType,
  TransferType,
  useCreateDepositoryAccountMutation,
  useCreateRawTransactionMutation,
  useCreateTransactionMutation,
  useDeleteRawTransactionsMutation,
} from '@transactions/graphql';
import {today} from 'common/date';
import Money from 'common/Money';
import * as React from 'react';
import {AmountDisplay} from 'src/AmountDisplay';
import {Button} from 'src/Button';
import CategoryPicker from 'src/CategoryPicker';
import DatePicker from 'src/DatePicker/DatePicker';
import {useMoneyFormatter} from 'src/hooks/useUnits';
import InputGroup from 'src/InputGroup';
import AmountInput from 'src/inputs/AmountInput';
import PayeeInput from 'src/inputs/PayeeInput';
import {RawTransactionPicker} from 'src/inputs/RawTransactionPicker';
import {TextInput} from 'src/inputs/TextInput';
import {Modal} from 'src/Modal';
import {SegmentedButton} from 'src/SegmentedButton';
import type {Option} from '@danwang/option';
import type {BasicRawTransactionFragment} from '@transactions/graphql';
import type {DateTime} from 'luxon';
import type {RawTransactionSelection} from 'src/inputs/RawTransactionPicker';
import type {SystemProps} from 'src/Modals';

/**
 * Balance-tracked accounts (banked credits and loans) are ordinary accounts;
 * these modals are macros over the primitive operations — create account,
 * create the inverse raw transaction, create the transfer / payment — each of
 * which can still be performed independently through the existing flows.
 */

type TrackedKind = 'credit' | 'loan';

const centsOf = (amount: Option<Money>): number =>
  amount.map((m) => Math.abs(m.amount)).getOrElse(0);

// ==================== Track as credit / loan ====================

export type TrackCreditModalUserProps = {
  rawTransaction: BasicRawTransactionFragment;
  onCreate?: (data: {
    rawTransactionIds: string[];
    updatedRawTransactions?: BasicRawTransactionFragment[];
  }) => void;
};

export const TrackCreditModal = (
  props: TrackCreditModalUserProps & SystemProps,
) => {
  const formId = React.useId();
  const formatMoney = useMoneyFormatter();
  const {onRequestClose, onCreate, rawTransaction} = props;
  // The unconsumed portion is what can be tracked
  const remaining = rawTransaction.remainingAmount ?? rawTransaction.amount;

  // An inbound source cannot fund a credit (it would create a negative
  // stored-value balance); those default to — and are restricted to — loans
  const outbound = remaining < 0;
  const [kind, setKind] = React.useState<TrackedKind>(
    outbound ? 'credit' : 'loan',
  );
  const [name, setName] = React.useState('');
  const [amount, setAmount] = React.useState<Option<Money>>(
    some(Money.fromInt(Math.abs(remaining), rawTransaction.currency)),
  );

  const [createAccount, {loading: creatingAccount}] =
    useCreateDepositoryAccountMutation();
  const [createRawTransaction, {loading: creatingRaw}] =
    useCreateRawTransactionMutation();
  const [createTransaction, {loading: creatingTransaction}] =
    useCreateTransactionMutation();
  const [deleteRawTransactions] = useDeleteRawTransactionsMutation();
  const [error, setError] = React.useState<string | null>(null);
  const loading = creatingAccount || creatingRaw || creatingTransaction;

  const client = useApolloClient();
  const cents = centsOf(amount);
  // createDepositoryAccount hardcodes USD, so a non-USD source would mix
  // currencies inside one tracked balance — disallow rather than corrupt
  const usdSource = rawTransaction.currency === 'USD';
  const valid =
    usdSource && name.trim().length > 0 && cents > 0 && cents <= Math.abs(remaining);
  const isPartial = cents < Math.abs(remaining);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!valid) return;
    const run = async () => {
      const accountResult = await createAccount({
        variables: {
          name: name.trim(),
          // Credits are stored value (wallet); loans are receivables
          depositoryType:
            kind === 'credit' ? DepositoryType.Wallet : DepositoryType.Other,
          trackBalance: true,
        },
      });
      const accountId = accountResult.data?.createDepositoryAccount._id;
      if (!accountId) return;

      // Inverse of the (possibly partial) source: the tracked account
      // receives what the source account gave up
      const sourceSign = Math.sign(remaining);
      const inverseAmount = -sourceSign * cents;
      const rawResult = await createRawTransaction({
        variables: {
          props: {
            accountId,
            amount: inverseAmount,
            currency: rawTransaction.currency,
            date: rawTransaction.date,
            description: name.trim(),
          },
        },
      });
      const trackedRawId = rawResult.data?.createRawTransaction._id;
      if (!trackedRawId) return;

      // Allocation amounts are positive magnitudes; the server applies each
      // raw transaction's own sign
      let transferResult;
      try {
        transferResult = await createTransaction({
          variables: {
            rawTransactions: [rawTransaction._id, trackedRawId],
            date: rawTransaction.date,
            transferType: TransferType.Prepaid,
            // Explicit allocations only when the source is partially consumed
            allocations: isPartial
              ? [
                  {rawTransactionId: rawTransaction._id, amount: cents},
                  {rawTransactionId: trackedRawId, amount: cents},
                ]
              : undefined,
          },
        });
      } catch (e) {
        // Roll back the inverse raw so the tracked balance is not left
        // altered by a failed transfer (the empty account stays settled
        // and therefore invisible)
        await deleteRawTransactions({variables: {ids: [trackedRawId]}});
        setError(e instanceof Error ? e.message : 'Transfer failed');
        return;
      }
      if (!transferResult.data) return;

      // Surface the new account on the outstanding cards and accounts list
      void client.refetchQueries({include: ['TrackedBalances', 'AllAccounts']});
      onCreate?.({
        rawTransactionIds: [rawTransaction._id],
        updatedRawTransactions: isPartial
          ? [
              {
                ...rawTransaction,
                remainingAmount: remaining - sourceSign * cents,
              },
            ]
          : undefined,
      });
      onRequestClose();
    };
    void run();
  };

  return (
    <Modal
      onRequestClose={onRequestClose}
      size="md"
      header="Track as credit"
      footer={
        <>
          <Button variant="outline" onClick={onRequestClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            type="submit"
            disabled={!valid || loading}
            form={formId}
          >
            Track{' '}
            {amount
              .map((m) => formatMoney(m, {showCurrency: true}))
              .getOrElse('')}
          </Button>
        </>
      }
    >
      <form id={formId} onSubmit={handleSubmit} className="flex flex-col gap-2">
        <InputGroup label="Source">
          <div className="text-muted-foreground flex items-center gap-2 py-1 text-sm">
            <AmountDisplay
              variant="plain"
              amount={Money.fromInt(remaining, rawTransaction.currency)}
            />
            <span className="min-w-0 truncate">
              {rawTransaction.description}
            </span>
            <span className="ml-auto shrink-0">{rawTransaction.date}</span>
          </div>
        </InputGroup>
        <InputGroup label="Type">
          <SegmentedButton
            className="self-start"
            options={
              outbound
                ? [
                    {value: 'credit', label: 'Credit'},
                    {value: 'loan', label: 'Loan'},
                  ]
                : [{value: 'loan', label: 'Loan'}]
            }
            value={kind}
            onChange={(next) => setKind(next === 'loan' ? 'loan' : 'credit')}
          />
        </InputGroup>
        <InputGroup label="Name">
          <TextInput
            autoFocus
            value={name}
            onChange={setName}
            placeholder={
              kind === 'credit' ? 'Ocean House gift card' : 'Alex — ski house'
            }
          />
        </InputGroup>
        <InputGroup label="Amount set aside">
          <AmountInput value={amount} onChange={setAmount} />
        </InputGroup>
        {!usdSource && (
          <div className="text-destructive text-xs">
            Only USD transactions can be tracked for now.
          </div>
        )}
        {error && <div className="text-destructive text-xs">{error}</div>}
        {isPartial && valid && (
          <div className="text-muted-foreground text-xs">
            Creates a tracked account and a prepaid transfer. The remaining{' '}
            <AmountDisplay
              variant="plain"
              amount={Money.fromInt(
                Math.abs(remaining) - cents,
                rawTransaction.currency,
              )}
            />{' '}
            of this transaction stays in your inbox.
          </div>
        )}
      </form>
    </Modal>
  );
};

// ==================== Record use ====================

export type RecordUseModalUserProps = {
  accountId: string;
  accountName: string;
  currency: string;
  /** Derived tracked balance in cents (signed). */
  balance: number;
  /** Latest anchor date; resolutions dated at/before it would be ignored. */
  latestAnchorDate?: string | null;
  onDone?: () => void;
};

export const RecordUseModal = (
  props: RecordUseModalUserProps & SystemProps,
) => {
  const formId = React.useId();
  const formatMoney = useMoneyFormatter();
  const {
    onRequestClose,
    accountId,
    accountName,
    currency,
    balance,
    latestAnchorDate,
    onDone,
  } = props;

  const [amount, setAmount] = React.useState<Option<Money>>(
    some(Money.fromInt(Math.abs(balance), currency)),
  );
  const [date, setDate] = React.useState<DateTime>(today());
  // The derived balance only counts raws strictly after the latest anchor;
  // an earlier-dated use would report success but change nothing
  const dateAfterAnchor =
    latestAnchorDate == null || (date.toISODate() ?? '') > latestAnchorDate;
  const [payee, setPayee] = React.useState('');
  const [category, setCategory] = React.useState<Option<string>>(none);

  const [createRawTransaction, {loading: creatingRaw}] =
    useCreateRawTransactionMutation();
  const [createTransaction, {loading: creatingTransaction}] =
    useCreateTransactionMutation();
  const [deleteRawTransactions] = useDeleteRawTransactionsMutation();
  const [error, setError] = React.useState<string | null>(null);
  const loading = creatingRaw || creatingTransaction;

  const cents = centsOf(amount);
  const valid =
    cents > 0 &&
    cents <= Math.abs(balance) &&
    dateAfterAnchor &&
    payee.trim().length > 0 &&
    !category.isEmpty;
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!valid) return;
    const run = async () => {
      const rawResult = await createRawTransaction({
        variables: {
          props: {
            accountId,
            amount: -Math.sign(balance) * cents,
            currency,
            date: date.toISODate() ?? '',
            description: payee.trim(),
          },
        },
      });
      const rawId = rawResult.data?.createRawTransaction._id;
      if (!rawId) return;

      try {
        await createTransaction({
          variables: {
            rawTransactions: [rawId],
            date: date.toISODate() ?? '',
            payee: payee.trim(),
            category: category.getOrElse(''),
          },
        });
      } catch (e) {
        // Roll back the raw so the tracked balance is unchanged (e.g. a
        // required-field rejection from validateFields)
        await deleteRawTransactions({variables: {ids: [rawId]}});
        setError(e instanceof Error ? e.message : 'Failed to record');
        return;
      }
      onDone?.();
      onRequestClose();
    };
    void run();
  };

  return (
    <Modal
      onRequestClose={onRequestClose}
      size="md"
      header={`Record use — ${accountName}`}
      footer={
        <>
          <Button variant="outline" onClick={onRequestClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            type="submit"
            disabled={!valid || loading}
            form={formId}
          >
            Record expense
          </Button>
        </>
      }
    >
      <form id={formId} onSubmit={handleSubmit} className="flex flex-col gap-2">
        <div className="text-muted-foreground text-sm">
          {formatMoney(Money.fromInt(Math.abs(balance), currency), {
            showCurrency: true,
          })}{' '}
          remaining
        </div>
        <InputGroup label="Amount">
          <AmountInput value={amount} onChange={setAmount} />
        </InputGroup>
        <InputGroup label="Date">
          <DatePicker selectedDate={date} onChange={setDate} />
        </InputGroup>
        <InputGroup label="Payee">
          <PayeeInput value={payee} onChange={setPayee} />
        </InputGroup>
        <InputGroup label="Category">
          <CategoryPicker
            selectedCategory={category}
            onSelectCategory={setCategory}
          />
        </InputGroup>
        {!dateAfterAnchor && (
          <div className="text-destructive text-xs">
            Date must be after the latest balance anchor ({latestAnchorDate}).
          </div>
        )}
        {error && <div className="text-destructive text-xs">{error}</div>}
      </form>
    </Modal>
  );
};

// ==================== Record loan repayment ====================

export type RecordLoanRepaymentModalUserProps = {
  accountId: string;
  accountName: string;
  currency: string;
  /** Derived tracked balance in cents (signed). */
  balance: number;
  /** Latest anchor date; resolutions dated at/before it would be ignored. */
  latestAnchorDate?: string | null;
  onDone?: () => void;
};

export const RecordLoanRepaymentModal = (
  props: RecordLoanRepaymentModalUserProps & SystemProps,
) => {
  const formId = React.useId();
  const {
    onRequestClose,
    accountId,
    accountName,
    currency,
    balance,
    latestAnchorDate,
    onDone,
  } = props;

  const [selectedRawId, setSelectedRawId] = React.useState('');
  const [selectedRaw, setSelectedRaw] =
    React.useState<RawTransactionSelection | null>(null);
  const [amount, setAmount] = React.useState<Option<Money>>(
    some(Money.fromInt(Math.abs(balance), currency)),
  );

  const [createRawTransaction, {loading: creatingRaw}] =
    useCreateRawTransactionMutation();
  const [createTransaction, {loading: creatingTransaction}] =
    useCreateTransactionMutation();
  const [deleteRawTransactions] = useDeleteRawTransactionsMutation();
  const [repayError, setRepayError] = React.useState<string | null>(null);
  const loading = creatingRaw || creatingTransaction;

  const cents = centsOf(amount);
  // The picked raw must flow in the repayment direction (same sign as the
  // outstanding balance) and its UNCONSUMED portion must cover the amount,
  // or the transfer cannot net to zero and would strand an unallocated raw
  // on the tracked account.
  const sourceRemaining =
    selectedRaw === null
      ? 0
      : Math.abs(selectedRaw.remainingAmount ?? selectedRaw.amount);
  const valid =
    selectedRaw !== null &&
    // A repayment must come from another account — pairing a raw from the
    // tracked account itself would consume it while netting to zero
    selectedRaw.accountId !== accountId &&
    // Repayment raws dated at/before the anchor would not affect the balance
    (latestAnchorDate == null || selectedRaw.date > latestAnchorDate) &&
    selectedRaw.currency === currency &&
    Math.sign(selectedRaw.amount) === Math.sign(balance) &&
    cents > 0 &&
    cents <= Math.abs(balance) &&
    cents <= sourceRemaining;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!valid || selectedRaw === null) return;
    const run = async () => {
      // The loan account gives up the receivable that the picked incoming
      // transaction repays
      const rawResult = await createRawTransaction({
        variables: {
          props: {
            accountId,
            amount: -Math.sign(balance) * cents,
            currency,
            date: selectedRaw.date,
            description: `Repayment — ${accountName}`,
          },
        },
      });
      const loanRawId = rawResult.data?.createRawTransaction._id;
      if (!loanRawId) return;

      const partialSource =
        cents < Math.abs(selectedRaw.remainingAmount ?? selectedRaw.amount);
      // Allocation amounts are positive magnitudes; the server applies each
      // raw transaction's own sign
      let transferResult;
      try {
        transferResult = await createTransaction({
          variables: {
            rawTransactions: [selectedRaw._id, loanRawId],
            date: selectedRaw.date,
            transferType: TransferType.AccountTransfer,
            allocations: partialSource
              ? [
                  {rawTransactionId: selectedRaw._id, amount: cents},
                  {rawTransactionId: loanRawId, amount: cents},
                ]
              : undefined,
          },
        });
      } catch (e) {
        await deleteRawTransactions({variables: {ids: [loanRawId]}});
        setRepayError(e instanceof Error ? e.message : 'Transfer failed');
        return;
      }
      if (!transferResult.data) return;
      onDone?.();
      onRequestClose();
    };
    void run();
  };

  return (
    <Modal
      onRequestClose={onRequestClose}
      size="md"
      header={`Record repayment — ${accountName}`}
      footer={
        <>
          <Button variant="outline" onClick={onRequestClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            type="submit"
            disabled={!valid || loading}
            form={formId}
          >
            Record repayment
          </Button>
        </>
      }
    >
      <form id={formId} onSubmit={handleSubmit} className="flex flex-col gap-2">
        <div className="text-muted-foreground text-sm">
          <AmountDisplay
            variant="plain"
            amount={Money.fromInt(Math.abs(balance), currency)}
          />{' '}
          outstanding
        </div>
        <InputGroup label="Incoming transaction">
          <RawTransactionPicker
            value={selectedRawId}
            includeProcessed={false}
            onChange={(id, transaction) => {
              setSelectedRawId(id);
              setSelectedRaw(transaction ?? null);
              if (transaction) {
                setAmount(
                  some(
                    Money.fromInt(
                      Math.min(
                        Math.abs(balance),
                        Math.abs(
                          transaction.remainingAmount ?? transaction.amount,
                        ),
                      ),
                      currency,
                    ),
                  ),
                );
              }
            }}
          />
        </InputGroup>
        <InputGroup label="Amount repaid">
          <AmountInput value={amount} onChange={setAmount} />
        </InputGroup>
        <div className="text-muted-foreground text-xs">
          Pairs the incoming transaction with the loan account as a transfer.
          For cash repayments, add a raw transaction to the receiving account
          first.
        </div>
        {repayError && (
          <div className="text-destructive text-xs">{repayError}</div>
        )}
      </form>
    </Modal>
  );
};
